import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  GuideBulletList,
  GuideIntroText,
  GuideStepProgress,
  GuideTipBlock,
} from './GuideTourPrimitives';
import { evidenceCheckpointAccent, guideTourTokens } from './guidedTourTheme';

export function CheckpointPopoverGuide({
  findingTitle,
  guideIntro,
  guidePoints,
  issueNote,
  tip,
  stepIndex,
  totalSteps,
  isRedFlag = false,
  onBack,
  onNext,
  onClose,
}: {
  findingTitle: string;
  guideIntro: string;
  guidePoints: string[];
  issueNote?: string;
  tip?: string;
  stepIndex: number;
  totalSteps: number;
  isRedFlag?: boolean;
  onBack: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const theme = useTheme();
  const tokens = guideTourTokens('evidence-checkpoint', theme);
  const accent = evidenceCheckpointAccent(isRedFlag, theme);
  const isLast = stepIndex >= totalSteps - 1;

  return (
    <Box
      sx={{
        mb: 1.1,
        p: 1.1,
        borderRadius: 2,
        border: '1px solid',
        borderColor: isRedFlag ? alpha(accent, 0.38) : tokens.accentBorder,
        bgcolor: isRedFlag ? alpha(accent, 0.05) : tokens.accentSoft,
      }}
    >
      <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ mb: 0.85 }}>
        <FlagRoundedIcon sx={{ fontSize: 17, color: accent, mt: 0.15, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <GuideStepProgress stepIndex={stepIndex} totalSteps={totalSteps} tokens={{ ...tokens, accent }} />
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent, display: 'block', lineHeight: 1.3, mb: 0.35 }}>
            {findingTitle}
          </Typography>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.35, mb: 0.35 }}>
            {guideIntro}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label="Close evidence guide"
          onClick={onClose}
          sx={{ mt: -0.25, mr: -0.25, color: 'text.secondary' }}
        >
          <CloseRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Stack>

      {issueNote ? (
        <Box
          sx={{
            mb: 0.75,
            p: 0.75,
            borderRadius: 1.25,
            bgcolor: alpha(accent, theme.palette.mode === 'light' ? 0.06 : 0.1),
            border: '1px solid',
            borderColor: alpha(accent, 0.2),
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: accent, display: 'block', mb: 0.25 }}>
            What flagged
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.5, fontWeight: 500 }}>
            {issueNote}
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ mb: tip ? 0.75 : 0 }}>
        <GuideIntroText>Review these points against the values below.</GuideIntroText>
        <GuideBulletList items={guidePoints} dotColor={accent} />
      </Box>

      {tip ? <GuideTipBlock text={tip} tokens={tokens} /> : null}

      {totalSteps > 1 ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={0.65}
          sx={{ mt: 0.95, pt: 0.75, borderTop: '1px dashed', borderColor: alpha(accent, 0.22) }}
        >
          <Box
            component="button"
            type="button"
            onClick={onBack}
            disabled={stepIndex === 0}
            aria-label="Previous check"
            sx={{
              border: '1px solid',
              borderColor: tokens.accentBorder,
              borderRadius: 1.25,
              px: 0.95,
              py: 0.35,
              font: 'inherit',
              fontSize: '0.75rem',
              fontWeight: 700,
              bgcolor: 'background.paper',
              color: 'text.secondary',
              cursor: stepIndex === 0 ? 'default' : 'pointer',
              opacity: stepIndex === 0 ? 0.45 : 1,
            }}
          >
            Back
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onNext}
            aria-label={isLast ? 'Finish evidence review' : 'Next check'}
            sx={{
              border: '1px solid',
              borderColor: alpha(accent, 0.45),
              borderRadius: 1.25,
              px: 0.95,
              py: 0.35,
              font: 'inherit',
              fontSize: '0.75rem',
              fontWeight: 700,
              bgcolor: alpha(accent, theme.palette.mode === 'light' ? 0.12 : 0.2),
              color: accent,
              cursor: 'pointer',
            }}
          >
            {isLast ? 'Done' : 'Next →'}
          </Box>
        </Stack>
      ) : null}
    </Box>
  );
}
